import React, { useState, useEffect, useRef, useCallback } from "react";
import Header from "../Components/Header";
import SubHeader from "../Components/SubHeader";
import Footer from "../Components/Footer";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import trData from "../Assets/tr.json";
import AfisEkleModal from "../Components/AfisEkleModal";
import { SlidersHorizontal, Upload, WandSparkles, X, Check, CalendarDays, MapPin, Users, ImageOff } from "lucide-react";
import "../Styles/ofislerimiz.css";
import "../Styles/afisler.css";

// Afiş tarihi: backend MySQL tarihini UTC ISO olarak gönderiyor (TR gece yarısı = önceki
// günün 21:00Z'si). Europe/Istanbul'a göre biçimleyince doğru gün çıkar.
const TR_SAAT_DILIMI = "Europe/Istanbul";
const formatAfisTarihi = (deger) => {
  if (!deger) return null;
  const tarih = new Date(deger);
  if (isNaN(tarih.getTime())) return String(deger);
  const gun = tarih.toLocaleDateString("tr-TR", {
    timeZone: TR_SAAT_DILIMI,
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const saat = tarih.toLocaleTimeString("tr-TR", {
    timeZone: TR_SAAT_DILIMI,
    hour: "2-digit",
    minute: "2-digit",
  });
  return saat === "00:00" ? gun : `${gun} · ${saat}`;
};

// Kategori kodları (Telegram botu / eski kayıtlar) → mobil uygulamadaki etiketler
const KATEGORI_ETIKETLERI = {
  SIRKET_WEBINAR_OPP_DIGER: "ŞİRKET: WEBİNAR-OPP-DİĞER",
  UT_WEBINAR: "UT: WEBİNAR",
  UT_KAFELER_OTELLER: "UT: KAFELER-OTELLER",
  UT_NDO_AKADEMI: "UT: NDO-AKADEMİ",
  UT_AVRUPA: "UT: AVRUPA",
  UT_EGITIMLER_VIDEOLAR: "UT: EĞİTİMLER-VİDEOLAR",
  UT_GUNLUK_BULTEN: "UT: GÜNLÜK BÜLTEN",
  KAFESUNUMU: "Kafe Sunumu",
  "NDO/AKADEMI": "NDO / Akademi",
  "OPP/İŞ FIRSAT": "OPP / İş Fırsatı",
  IS_FIRSAT: "İş Fırsatı",
  BBS: "BBS",
};

const kategoriEtiketi = (kod) => {
  const ham = String(kod || "").replace(/^#/, "").trim();
  if (!ham) return "";
  const etiket = KATEGORI_ETIKETLERI[ham.toUpperCase()] || KATEGORI_ETIKETLERI[ham.toLocaleUpperCase("tr-TR")];
  if (etiket) return etiket;
  // Bilinmeyen kod (BOSLUKSUZ_ALT_CIZGILI): alt çizgileri boşluk yap, baş harfleri büyüt
  if (ham.includes("_") && !/\s/.test(ham)) {
    return ham
      .split("_")
      .filter(Boolean)
      .map((p) => p.charAt(0).toLocaleUpperCase("tr-TR") + p.slice(1).toLocaleLowerCase("tr-TR"))
      .join(" ");
  }
  return ham;
};

const kategoriListesi = (kategoriler) => {
  if (Array.isArray(kategoriler)) return kategoriler;
  if (typeof kategoriler === "string" && kategoriler) {
    try {
      const p = JSON.parse(kategoriler);
      return Array.isArray(p) ? p : [p];
    } catch (e) {
      return [kategoriler];
    }
  }
  return [];
};

const afisGorselUrl = (fotograf, token) =>
  fotograf && fotograf.startsWith("http")
    ? fotograf
    : `${process.env.REACT_APP_API_URL}/api/files/?filepath=${encodeURIComponent(fotograf || "")}&token=${encodeURIComponent(token || "")}`;

export default function Afisler() {
  const [posters, setPosters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [katilimBilgisi, setKatilimBilgisi] = useState([]);

  // Filtreleme State'leri
  const [showFilter, setShowFilter] = useState(false);
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedCommunities, setSelectedCommunities] = useState([]);
  const [activeFilter, setActiveFilter] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [toastMessage, setToastMessage] = useState("");
  const [viewMode, setViewMode] = useState("all");
  const [showEkleModal, setShowEkleModal] = useState(false);
  const [islemdekiAfis, setIslemdekiAfis] = useState(null);

  const limit = 10;
  const navigate = useNavigate();
  const observer = useRef();

  const lastElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setOffset(prev => prev + limit);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  const fetchPosters = async (currentOffset, filterToUse = activeFilter, mode = viewMode) => {
    const token = Cookies.get("token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      setLoading(true);
      let url = `${process.env.REACT_APP_API_URL}/api/afis/getir`;
      let requestBody = { token, offset: currentOffset, limit, filter: filterToUse };

      if (mode === "joined") {
        url = `${process.env.REACT_APP_API_URL}/api/afis/katildiklarim`;
        requestBody = { token, offset: currentOffset, limit };
      }

      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });
      const data = await response.json();

      if (data.m === "basarili") {
        const newData = data.data || data.datas || [];
        setPosters(prev => {
          if (currentOffset === 0) return newData;
          const newIds = newData.map(p => p.id_afis);
          const filteredPrev = prev.filter(p => !newIds.includes(p.id_afis));
          return [...filteredPrev, ...newData];
        });

        if (newData.length < limit || (data.uzunluk && data.uzunluk[0] && data.uzunluk[0].uzunluk <= currentOffset + limit) || (data.sayi && data.sayi[0] && data.sayi[0].afisSayisi <= currentOffset + limit)) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }

        // Katılım bilgisini çek
        const idList = newData.map(item => item.id_afis);
        if (idList.length > 0) {
          fetchKatilimBilgisi(token, idList);
        }
      } else {
        setHasMore(false);
        if (currentOffset === 0) setPosters([]);
      }
    } catch (error) {
      console.error("Afişler yüklenirken hata:", error);
      if (currentOffset === 0) setPosters([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchKatilimBilgisi = async (token, id_afisler) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/afis/katilimBilgisi`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, id_afisler }),
      });
      const data = await res.json();
      const katilimData = data.data || data.datas;
      if (data.m === "basarili" && katilimData) {
        setKatilimBilgisi(prev => {
          const ids = katilimData.map(k => k.id_afis);
          const combined = new Set([...prev, ...ids]);
          return Array.from(combined);
        });
      }
    } catch (e) {
      console.error("Katılım bilgisi hatası:", e);
    }
  };

  // Filtre veya sekme değiştiğinde offset'i sıfırlayıp baştan çek
  useEffect(() => {
    setOffset(0);
    setPosters([]);
    setHasMore(true);
    fetchPosters(0, activeFilter, viewMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, viewMode]);

  // Sonsuz kaydırma için offset değiştiğinde daha fazla veri çek
  useEffect(() => {
    if (offset > 0) {
      fetchPosters(offset, activeFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  const showToast = (message) => {
    setToastMessage(message);
    setTimeout(() => {
      setToastMessage("");
    }, 3000);
  };

  const handleKatil = async (afisId) => {
    const token = Cookies.get("token");
    if (!token) {
      navigate("/login");
      return;
    }
    if (islemdekiAfis !== null) return;
    setIslemdekiAfis(afisId);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/afis/katil-afis`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, id_afis: afisId }),
      });
      const data = await response.json();
      
      if (data.m === "basarili") {
        // Kartı anında güncelle: katıldı durumu + kişi sayısı +1
        setKatilimBilgisi(prev => (prev.includes(afisId) ? prev : [...prev, afisId]));
        setPosters(prev => prev.map(p => p.id_afis === afisId ? { ...p, katilimci: (Number(p.katilimci) || 0) + 1 } : p));
        showToast("Afişe katılındı.");
      } else if (data.m === "Zaten katılımcısın.") {
        // Sunucuda zaten katılımcı: ekranı sunucuyla eşitle (sayı zaten sayılmış)
        setKatilimBilgisi(prev => (prev.includes(afisId) ? prev : [...prev, afisId]));
        showToast("Bu afişe zaten katılıyorsunuz.");
      } else if (data.m === "Bu afişin sahibi katılamaz") {
        showToast("Kendi afişinize katılamazsınız.");
      } else {
        showToast(data.m || "Katılım başarısız oldu.");
      }
    } catch (e) {
      console.log("Katılım Hatası:", e);
      showToast("Bir hata oluştu. Lütfen tekrar deneyin.");
    } finally {
      setIslemdekiAfis(null);
    }
  };

  const handleAyril = async (afisId) => {
    const token = Cookies.get("token");
    if (!token) return;
    if (islemdekiAfis !== null) return;
    setIslemdekiAfis(afisId);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/afis/ayril-afis`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, id_afis: afisId }),
      });
      const data = await response.json();
      
      if (data.m === "basarili") {
        setKatilimBilgisi(prev => prev.filter(id => id !== afisId));
        if (viewMode === "joined") {
          // "Katıldıklarım" listesinden çıkar
          setPosters(prev => prev.filter(p => p.id_afis !== afisId));
        } else {
          // "Tüm Afişler"de kart kalır: Katıl'a döner, kişi sayısı -1
          setPosters(prev => prev.map(p => p.id_afis === afisId ? { ...p, katilimci: Math.max(0, (Number(p.katilimci) || 0) - 1) } : p));
        }
        showToast("Afişten vazgeçildi.");
      } else if (data.m === "Zaten afişe katılmıyorsun.") {
        setKatilimBilgisi(prev => prev.filter(id => id !== afisId));
        if (viewMode === "joined") setPosters(prev => prev.filter(p => p.id_afis !== afisId));
        showToast("Bu afişe zaten katılmıyorsunuz.");
      } else {
        showToast(data.m || "Vazgeçme işlemi başarısız oldu.");
      }
    } catch (e) {
      console.log("Ayrılma Hatası:", e);
      showToast("Bir hata oluştu.");
    } finally {
      setIslemdekiAfis(null);
    }
  };

  const applyFilter = () => {
    let newFilter = null;
    const hasLocation = selectedCity || selectedDistrict;
    const hasCategory = selectedCategories.length > 0;
    const hasCommunity = selectedCommunities.length > 0;

    if (hasLocation || hasCategory || hasCommunity) {
      newFilter = {};

      // Lokasyon
      if (hasLocation) {
        newFilter.lokasyon = {
          ulke: "Türkiye",
          sehir: selectedCity || "",
          ilce: selectedDistrict || ""
        };
      } else {
        newFilter.lokasyon = { ulke: "", sehir: "", ilce: "" };
      }

      // Topluluk ve Kategori
      newFilter.topluluk = selectedCommunities;
      // Kategori adlarının başındaki "#" işaretini backend formatına uygun olması için kaldırıyoruz
      newFilter.kategori = selectedCategories.map(c => c.replace("#", ""));
    }

    setActiveFilter(newFilter);
    setShowFilter(false);
  };

  const clearFilter = () => {
    setSelectedCity("");
    setSelectedDistrict("");
    setSelectedCategories([]);
    setSelectedCommunities([]);
    setActiveFilter(null);
    setShowFilter(false);
  };

  const categoriesList = [
    { id: 1, label: '#UT: KAFELER-OTELLER' },
    { id: 2, label: '#ŞİRKET: WEBİNAR-OPP-DİĞER' },
    { id: 3, label: '#UT: NDO-AKADEMİ' },
    { id: 4, label: '#UT: AVRUPA' },
    { id: 5, label: '#UT: WEBİNAR' },
    { id: 6, label: '#UT: EĞİTİMLER-VİDEOLAR' },
    { id: 7, label: '#UT: GÜNLÜK BÜLTEN' },
  ];

  const communitiesList = [
    'Şirket',
    'Universal team',
    'Ekibim',
    'Referenslarım'
  ];

  const toggleCategory = (label) => {
    setSelectedCategories(prev =>
      prev.includes(label) ? prev.filter(c => c !== label) : [...prev, label]
    );
  };

  const toggleCommunity = (label) => {
    setSelectedCommunities(prev =>
      prev.includes(label) ? prev.filter(c => c !== label) : [...prev, label]
    );
  };


  const selectedCityData = trData.find(c => c.text === selectedCity);
  const districts = selectedCityData ? selectedCityData.districts : [];

  const aktifFiltreSayisi = activeFilter
    ? (activeFilter.lokasyon?.sehir ? 1 : 0) + (activeFilter.kategori?.length || 0) + (activeFilter.topluluk?.length || 0)
    : 0;

  return (
    <div className="af-sayfa">
      <SubHeader />
      <Header />

      <div className="offices-header af-bant">
        <div className="offices-header-pattern" />
        <div className="container offices-header-container text-center">
          <h6 className="offices-header-subtitle">🖼️ AFİŞLER</h6>
          <h1 className="offices-header-title">Afişler</h1>
          <p className="offices-header-description">
            Yaklaşan etkinlik afişlerini inceleyin, katılın ya da kendi afişinizi paylaşın.
          </p>
        </div>
      </div>

      <div className="container af-icerik">
        {/* Araç çubuğu */}
        <div className="af-arac">
          <div className="af-segment" role="tablist">
            <button type="button" className={viewMode === "all" ? "aktif" : ""} onClick={() => setViewMode("all")}>
              Tüm Afişler
            </button>
            <button type="button" className={viewMode === "joined" ? "aktif" : ""} onClick={() => setViewMode("joined")}>
              Katıldıklarım
            </button>
          </div>

          <div className="af-arac-sag">
            {viewMode === "all" && (
              <button type="button" className={`af-btn af-btn-ikincil${showFilter ? " acik" : ""}`} onClick={() => setShowFilter(!showFilter)}>
                <SlidersHorizontal size={16} /> Filtrele
                {aktifFiltreSayisi > 0 && <span className="af-sayac">{aktifFiltreSayisi}</span>}
              </button>
            )}
            <button type="button" className="af-btn af-btn-ikincil" onClick={() => setShowEkleModal(true)}>
              <Upload size={16} /> Afiş Ekle
            </button>
            <button type="button" className="af-btn af-btn-ana" onClick={() => navigate("/afis-olusturucu")}>
              <WandSparkles size={16} /> Afiş Oluştur
            </button>
          </div>
        </div>

        {/* Aktif filtreler */}
        {activeFilter && aktifFiltreSayisi > 0 && (
          <div className="af-etiketler">
            {activeFilter.lokasyon?.sehir && (
              <span className="af-etiket">
                {activeFilter.lokasyon.sehir}{activeFilter.lokasyon?.ilce ? ` / ${activeFilter.lokasyon.ilce}` : ""}
              </span>
            )}
            {activeFilter.kategori && activeFilter.kategori.map((k, i) => (
              <span key={`cat-${i}`} className="af-etiket">#{kategoriEtiketi(k)}</span>
            ))}
            {activeFilter.topluluk && activeFilter.topluluk.map((t, i) => (
              <span key={`top-${i}`} className="af-etiket">{t}</span>
            ))}
            <button type="button" className="af-etiket-temizle" onClick={clearFilter}>
              <X size={14} /> Filtreleri temizle
            </button>
          </div>
        )}

        {/* Filtre paneli */}
        {showFilter && (
          <div className="af-filtre">
            <div className="af-filtre-ust">
              <h5>Afişleri filtrele</h5>
              <button type="button" className="af-kapat" onClick={() => setShowFilter(false)} aria-label="Kapat"><X size={18} /></button>
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <label className="af-filtre-etiket">Şehir</label>
                <select
                  className="form-select"
                  value={selectedCity}
                  onChange={(e) => {
                    setSelectedCity(e.target.value);
                    setSelectedDistrict(""); // Şehir değişince ilçeyi sıfırla
                  }}
                >
                  <option value="">Tüm Şehirler</option>
                  {trData.map(city => (
                    <option key={city.value} value={city.text}>{city.text}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="af-filtre-etiket">İlçe</label>
                <select
                  className="form-select"
                  value={selectedDistrict}
                  onChange={(e) => setSelectedDistrict(e.target.value)}
                  disabled={!selectedCity}
                >
                  <option value="">Tüm İlçeler</option>
                  {districts.map(district => (
                    <option key={district.value} value={district.text}>{district.text}</option>
                  ))}
                </select>
              </div>
            </div>

            <label className="af-filtre-etiket mt-4">Kategori</label>
            <div className="af-secenekler">
              {categoriesList.map(cat => (
                <button
                  type="button"
                  key={cat.id}
                  className={`af-secenek${selectedCategories.includes(cat.label) ? " secili" : ""}`}
                  onClick={() => toggleCategory(cat.label)}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            <label className="af-filtre-etiket mt-4">Topluluk</label>
            <div className="af-secenekler">
              {communitiesList.map(com => (
                <button
                  type="button"
                  key={com}
                  className={`af-secenek${selectedCommunities.includes(com) ? " secili" : ""}`}
                  onClick={() => toggleCommunity(com)}
                >
                  {com}
                </button>
              ))}
            </div>

            <div className="af-filtre-alt">
              <button type="button" className="af-btn af-btn-ikincil" onClick={clearFilter}>Temizle</button>
              <button type="button" className="af-btn af-btn-mavi" onClick={applyFilter}><Check size={16} /> Uygula</button>
            </div>
          </div>
        )}

        {/* Afiş listesi */}
        <div className="af-izgara">
          {posters.map((afis, index) => {
            const isLast = posters.length === index + 1;
            // "Katıldıklarım" listesindeki her afişe katılınmıştır
            const isKatildi = viewMode === "joined" || katilimBilgisi.includes(afis.id_afis);
            const islemde = islemdekiAfis === afis.id_afis;
            const imageUrl = afisGorselUrl(afis.fotograf, Cookies.get("token"));
            const kategoriler = kategoriListesi(afis.kategoriler);

            return (
              <div className="af-kart" key={afis.id_afis} ref={isLast ? lastElementRef : null}>
                <button type="button" className="af-gorsel" onClick={() => setSelectedIndex(index)} aria-label="Afişi büyüt">
                  <img
                    src={imageUrl}
                    alt={afis.baslik}
                    loading="lazy"
                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/Assets/Images/ut.png'; e.currentTarget.parentElement.classList.add('yedek'); }}
                  />
                </button>

                <div className="af-govde">
                  <h3 className="af-baslik">{afis.baslik || "Başlıksız Afiş"}</h3>

                  <ul className="af-bilgi">
                    <li><CalendarDays size={15} />{formatAfisTarihi(afis.tarih) || "Tarih belirtilmedi"}</li>
                    <li><MapPin size={15} />{afis.sehir ? `${afis.sehir}${afis.ilce ? ` / ${afis.ilce}` : ''}` : "Konum belirtilmedi"}</li>
                    <li><Users size={15} />{afis.katilimci || 0} kişi katılıyor</li>
                  </ul>

                  {kategoriler.length > 0 && (
                    <div className="af-kategoriler">
                      {kategoriler.map((kat, i) => (
                        <span key={i}>#{kategoriEtiketi(kat)}</span>
                      ))}
                    </div>
                  )}

                  {isKatildi ? (
                    <button
                      type="button"
                      disabled={islemde}
                      className="af-katil af-katil-vazgec"
                      onClick={(e) => {
                        e.preventDefault();
                        handleAyril(afis.id_afis);
                      }}
                    >
                      <span className="af-katil-durum"><Check size={16} /> Katılıyorsun</span>
                      <span className="af-katil-eylem">Vazgeç</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={islemde}
                      className="af-katil"
                      onClick={(e) => {
                        e.preventDefault();
                        handleKatil(afis.id_afis);
                      }}
                    >
                      Katıl
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {loading && (
          <div className="af-durum"><div className="af-spinner" /></div>
        )}

        {!hasMore && posters.length > 0 && (
          <div className="af-durum af-durum-yazi">Tüm afişler yüklendi.</div>
        )}

        {!loading && posters.length === 0 && (
          <div className="af-bos">
            <ImageOff size={44} strokeWidth={1.5} />
            <h4>{viewMode === "joined" ? "Henüz katıldığınız afiş yok" : "Henüz afiş bulunmuyor"}</h4>
            <p>
              {viewMode === "joined"
                ? "Katıldığınız afişler burada listelenir."
                : "Bu filtreye uygun afiş bulunamadı veya henüz eklenmedi."}
            </p>
          </div>
        )}
      </div>

      {/* Afiş Ekle (Paylaş) Modal */}
      {showEkleModal && (
        <AfisEkleModal
          onClose={() => setShowEkleModal(false)}
          onSuccess={() => {
            setOffset(0);
            setPosters([]);
            setHasMore(true);
            fetchPosters(0, activeFilter, viewMode);
            showToast("Yeni afiş başarıyla eklendi.");
          }}
        />
      )}

      {/* Büyük afiş */}
      {selectedIndex !== null && posters[selectedIndex] && (
        <div className="af-isik" onClick={() => setSelectedIndex(null)}>
          <button type="button" className="af-isik-kapat" onClick={() => setSelectedIndex(null)} aria-label="Kapat">
            <X size={22} />
          </button>
          <img
            src={afisGorselUrl(posters[selectedIndex].fotograf, Cookies.get("token"))}
            alt={posters[selectedIndex].baslik || "Büyük Afiş"}
            onClick={(e) => e.stopPropagation()}
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/Assets/Images/ut.png'; }}
          />
        </div>
      )}

      {/* Bildirim */}
      {toastMessage && <div className="af-bildirim">{toastMessage}</div>}

      <Footer />
    </div>
  );
}
